import React, { FunctionComponent } from "react";
import { Link } from "react-router-dom";
import { ListItem, ListItemButton, ListItemIcon, ListItemText, Box } from "@mui/material";
import { PremiumFeatureTooltip } from "components";
import { ExpandableMenuOptions } from "./components";
import { SidebarItemProps } from "./types";
import { listItemStyles, listItemButtonStyles, iconContainerStyles, listItemIconStyles, listItemTextStyles, sidebarTitleStyles, sidebarTitleTextStyles } from "./styles";

/**
 * SidebarItem component renders a sidebar navigation item with support for:
 * - Regular navigation items with optional links
 * - Expandable menu items (Documents, Resources)
 * - Collapsible sidebar behavior
 * - Selected state styling
 * 
 * @param isOpen - Whether the sidebar is expanded
 * @param icon - Icon to display
 * @param title - Text label for the item
 * @param path - Optional navigation path (makes item clickable)
 * @param isSelected - Whether this item is currently selected
 * @param renderMenu - Whether to render as expandable menu
 * @param sidebarTitle - Optional subtitle when sidebar is collapsed
 */
export const SidebarItem: FunctionComponent<SidebarItemProps> = ({
  isOpen,
  icon,
  title,
  path,
  isSelected,
  renderMenu,
  sidebarTitle,
  isEnabled = true,
  premiumFeatureTooltipInfoMessage,
  trackEventFn,
}) => {
  const handleTrackEvent = (currentLocation: string) => {
    if (currentLocation === "Check Missing") {
      trackEventFn("Viewed Missing Data Tab", "Documents - Check Missing");
    }

    if (currentLocation === "Dashboard") {
      trackEventFn("Viewed Emissions Dashboard", "Emissions - Dashboard");
    } else {
      trackEventFn(`Clicked ${currentLocation} Tab`, `${currentLocation}`);
    }
  };

  if (renderMenu) {
    return (
      <ExpandableMenuOptions
        isOpen={isOpen}
        icon={icon}
        isSelected={isSelected}
        title={title}
        onClick={(currentLocation: string) => handleTrackEvent(currentLocation)}
      />
    );
  }

  const content = (
    <Link
      to={isEnabled ? path : '#'}
      style={{
        color: "inherit",
        textDecoration: "none",
      }}
      onClick={(e) => {
        if (!isEnabled) {
          e.preventDefault();
        }
        handleTrackEvent(title);
      }}
    >
      <ListItem sx={listItemStyles(isOpen)}>
        <ListItemButton
          sx={listItemButtonStyles(isOpen, isSelected)}
          selected={isSelected}
          disabled={!isEnabled}
        >
          <Box sx={iconContainerStyles}>
            <ListItemIcon sx={listItemIconStyles(isOpen)}>
              {icon}
            </ListItemIcon>
            <ListItemText
              primary={title}
              sx={listItemTextStyles(isOpen)}
            />
          </Box>

          {!isOpen && sidebarTitle && (
            <Box sx={sidebarTitleStyles}>
              <Box sx={sidebarTitleTextStyles}>
                {sidebarTitle}
              </Box>
            </Box>
          )}
        </ListItemButton>
      </ListItem>
    </Link>
  );

  return !isEnabled ? (
    <PremiumFeatureTooltip placement="right" infoMessage={premiumFeatureTooltipInfoMessage}>
      {content}
    </PremiumFeatureTooltip>
  ) : content;
};
